import { zodResolver } from '@hookform/resolvers/zod'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Input,
} from '@racefront/shadcn-ui'
import { Loader2 } from 'lucide-react'
import { useEffect } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { FormattedMessage, useIntl } from 'react-intl'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { z } from 'zod'
import { APP_ROUTES } from '../../../constants/routes'
import { ApiError } from '../../../types/api-error.type'
import { useAuth } from '../../data/hooks/auth'
import { useAuthStore } from '../../data/stores/auth.store'
import { useSignInSchema } from './data/schema'
import { TERMS_URL } from '../../constants'
import { useFeatureFlag } from 'configcat-react'

export default function SignIn() {
  const intl = useIntl()
  const [params] = useSearchParams()
  const { setRedirectUri, context } = useAuthStore()
  const redirectUri = params.get('redirectUri') ?? undefined
  const mode = (params.get('mode') === 'phone' ? 'phone' : 'email') as 'email' | 'phone'
  const { initiateAuthSession, initiateAuthData, initiateAuthLoading, initiateAuthError } = useAuth()
  const signInSchema = useSignInSchema(mode)
  const nav = useNavigate()
  const form = useForm<z.infer<typeof signInSchema>>({
    resolver: zodResolver(signInSchema),
    defaultValues: {
      username: context?.username ?? '',
    },
  })
  const { value: enableOtpViaSms, loading: enableOtpViaSmsLoading } = useFeatureFlag('enableOtpViaSms', true)
  const onSubmit: SubmitHandler<z.infer<typeof signInSchema>> = async (data) => {
    initiateAuthSession(data.username, undefined, redirectUri)
  }

  const handleSwitchToEmail = () => {
    const searchParams = new URLSearchParams(params)
    searchParams.delete('mode')
    const queryString = searchParams.toString()
    nav(`${APP_ROUTES.SignIn}${queryString ? `?${queryString}` : ''}`)
  }

  const { isSubmitting } = form.formState

  const username = form.watch('username')

  useEffect(() => {
    if (!initiateAuthLoading && initiateAuthData) {
      if (initiateAuthData?.registrationToken) {
        // Navigate to signup with registration token, preserving redirectUri and role
        const signUpParams = new URLSearchParams(params)
        signUpParams.set('registrationToken', initiateAuthData.registrationToken)
        nav(`${APP_ROUTES.SignUp}?${signUpParams.toString()}`)
      } else {
        nav(APP_ROUTES.Verify)
      }
    }
  }, [initiateAuthLoading, initiateAuthData, nav, params])

  useEffect(() => {
    if (initiateAuthError) {
      const error = initiateAuthError as ApiError
      if (error.error === 'invalid_credential') {
        toast.error(
          intl.formatMessage({ id: 'errors.invalid_username', defaultMessage: 'Invalid username. Please try again' }),
        )
      } else {
        toast.error(
          intl.formatMessage({
            id: 'errors.general_retry_error',
            defaultMessage: "We couldn't find an account with that email. Try with another email or sign up",
          }),
        )
      }
    }
  }, [initiateAuthError, intl])

  useEffect(() => {
    if (redirectUri) {
      setRedirectUri(redirectUri)
    }
  }, [redirectUri, setRedirectUri])

  useEffect(() => {
    if (username) {
      const noSpaces = username.replace(/\s/g, '')
      if (noSpaces !== username) {
        form.setValue('username', noSpaces)
      }
    }
  }, [username, form])

  return (
    <>
      <Dialog open={mode === 'phone' && !enableOtpViaSms && !enableOtpViaSmsLoading}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              <FormattedMessage
                id="signin.phoneDisabled.title"
                defaultMessage="Phone sign-in temporarily unavailable"
              />
            </DialogTitle>
            <DialogDescription>
              <FormattedMessage
                id="signin.phoneDisabled.description"
                defaultMessage="We're currently unable to send codes via SMS. Please use email to sign in instead."
              />
            </DialogDescription>
          </DialogHeader>
          <Button onClick={handleSwitchToEmail} className="w-full">
            <FormattedMessage id="signin.phoneDisabled.useEmail" defaultMessage="Continue with email" />
          </Button>
        </DialogContent>
      </Dialog>
      <div className="flex w-full flex-col items-center">
        <h3 className="text-center text-2xl font-bold">
          <FormattedMessage id="signin.title" defaultMessage="Sign in to your account" />
        </h3>
        <div className="mt-4 text-center text-sm">
          <FormattedMessage id="signin.noAccount" defaultMessage="Don't have an account?" />{' '}
          <Link to={APP_ROUTES.SignUp + window.location.search} className="underline">
            <FormattedMessage id="signin.signUp" defaultMessage="Sign up" />
          </Link>
        </div>
        <div className="mt-8 w-full max-w-md">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="notranslate flex w-full flex-col gap-6">
              <FormField
                control={form.control}
                name="username"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        id="email"
                        autoFocus
                        autoComplete="username"
                        className="notranslate"
                        translate="no"
                        placeholder={intl.formatMessage({
                          id: mode === 'phone' ? 'signin.placeholder.phone' : 'signin.placeholder.email',
                          defaultMessage: mode === 'phone' ? 'Phone number' : 'Email',
                        })}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage id="email-message" />
                  </FormItem>
                )}
              />
              {mode === 'phone' && (
                <Button type="button" variant="link" onClick={handleSwitchToEmail} className="-mt-4">
                  <FormattedMessage id="signin.useEmail" defaultMessage="Use email instead" />
                </Button>
              )}
              <Button id="submit" disabled={isSubmitting} type="submit" className="mt-2 w-full">
                {(isSubmitting || initiateAuthLoading) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isSubmitting || initiateAuthLoading ? (
                  <FormattedMessage id="signin.signingIn" defaultMessage="Signing in..." />
                ) : (
                  <FormattedMessage id="signin.signIn" defaultMessage="Sign in" />
                )}
              </Button>

              <p className="text-muted-foreground text-center text-sm">
                <FormattedMessage
                  id="common.termsConditionsNotice"
                  defaultMessage="By submitting, you agree to our <a>Terms & Conditions</a>."
                  values={{
                    a: (chunks) => (
                      <a
                        href={TERMS_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-foreground underline"
                      >
                        {chunks}
                      </a>
                    ),
                  }}
                />
              </p>
            </form>
          </Form>
        </div>
      </div>
    </>
  )
}
